import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import api from '../services/api';
import PageContainer from '../components/ui/PageContainer';
import SearchableSelect from '../components/SearchableSelect';
import PremiumSelect from '../components/PremiumSelect';
import AlertModal from '../components/AlertModal';
import { User, Cycle, AlertState } from '../types';
import { isCycleClosedForSubmissions } from '../utils/cycleFeedbackUi';

interface ReverseFeedbackFormData {
  managerId: string;
  cycleId: string;
  clarityScore: string;
  clarityQuestion: 'sim' | 'parcialmente' | 'não';
  clarityObservations: string;
  availabilityScore: string;
  availabilityQuestion: 'sim' | 'parcialmente' | 'não';
  availabilityObservations: string;
  feedbackQualityScore: string;
  feedbackQualityQuestion: 'sim' | 'parcialmente' | 'não';
  feedbackQualityObservations: string;
  whatManagerDoesWell: string;
  whatCanImprove: string;
  suggestions: string;
  anonymous: boolean;
}

const CreateReverseFeedback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [formData, setFormData] = useState<ReverseFeedbackFormData>({
    managerId: '',
    cycleId: '',
    clarityScore: '3',
    clarityQuestion: 'sim',
    clarityObservations: '',
    availabilityScore: '3',
    availabilityQuestion: 'sim',
    availabilityObservations: '',
    feedbackQualityScore: '3',
    feedbackQualityQuestion: 'sim',
    feedbackQualityObservations: '',
    whatManagerDoesWell: '',
    whatCanImprove: '',
    suggestions: '',
    anonymous: false,
  });
  const yesPartialNoOptions = [
    { value: 'sim', label: 'Sim' },
    { value: 'parcialmente', label: 'Parcialmente' },
    { value: 'não', label: 'Não' },
  ];

  const normalizeNumberInput = (value: string): string => {
    if (value === '') return '';
    if (/^0+\d/.test(value)) return value.replace(/^0+/, '');
    return value;
  };

  const toNumber = (value: string): number => Number(value) || 0;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const cycleId = searchParams.get('cycleId');
    const managerId = searchParams.get('managerId');
    if (!cycleId || !managerId || cycles.length === 0 || allUsers.length === 0) return;
    const cycleOk = cycles.some((c) => c.id === cycleId);
    const managerOk = allUsers.some((u) => u.id === managerId);
    if (!cycleOk || !managerOk) return;
    setFormData((prev) =>
      prev.cycleId === cycleId && prev.managerId === managerId
        ? prev
        : { ...prev, cycleId, managerId },
    );
  }, [searchParams, cycles, allUsers]);

  const loadData = async (): Promise<void> => {
    try {
      const [managersRes, cyclesRes] = await Promise.all([
        api.get<User[]>('/users/managers-for-reverse'),
        api.get<Cycle[]>('/cycles'),
      ]);
      setAllUsers(managersRes.data.filter((u) => u.active));
      setCycles(
        cyclesRes.data
          .filter((c) => !isCycleClosedForSubmissions(c.deadlineDate))
          .sort((a, b) => Number(b.active) - Number(a.active) || new Date(b.startDate).getTime() - new Date(a.startDate).getTime()),
      );
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } },
  ): void => {
    const { name, value } = e.target;
    const type = 'type' in e.target ? (e.target as HTMLInputElement).type : undefined;
    const checked = 'checked' in e.target ? (e.target as HTMLInputElement).checked : false;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? normalizeNumberInput(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/feedbacks/reverse', {
        ...formData,
        clarityScore: toNumber(formData.clarityScore),
        availabilityScore: toNumber(formData.availabilityScore),
        feedbackQualityScore: toNumber(formData.feedbackQualityScore),
      });
      setAlert({
        isOpen: true,
        message: 'Feedback reverso criado com sucesso!',
        type: 'success',
      });
      setTimeout(() => {
        navigate('/feedbacks');
      }, 1500);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || 'Erro ao criar feedback reverso',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer
      icon={<ReplyOutlinedIcon />}
      title="Feedback reverso"
      subtitle="Registre sua percepção sobre clareza, disponibilidade e qualidade do acompanhamento do gestor no ciclo atual. Você pode optar por enviar de forma anônima. Só aparecem ciclos cujo prazo de envio ainda não passou (em geral 3 dias corridos após o fim do período); para reabrir, um administrador pode alterar o prazo limite do ciclo."
    >
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={{ xs: 2, md: 3 }}>
          {/* Contexto — Informações básicas */}
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Contexto
                </Typography>
                <Typography variant="h6" component="h2">
                  Informações básicas
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SearchableSelect
                    label="Usuário a avaliar"
                    fieldName="managerId"
                    options={allUsers}
                    value={formData.managerId}
                    onChange={handleChange}
                    placeholder="Selecione o usuário a ser avaliado..."
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PremiumSelect
                    id="reverse-cycle-id"
                    label="Ciclo"
                    value={formData.cycleId}
                    options={cycles.map((cycle) => ({ value: cycle.id, label: cycle.name }))}
                    onChange={(value) => handleChange({ target: { name: 'cycleId', value } })}
                    placeholder="Selecione..."
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="anonymous"
                        checked={formData.anonymous}
                        onChange={handleChange}
                      />
                    }
                    label="Feedback anônimo"
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          {/* Clareza técnica e direcionamento */}
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Clareza
                </Typography>
                <Typography variant="h6" component="h2">
                  Clareza técnica e direcionamento
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Score de clareza (1–5)"
                    name="clarityScore"
                    value={formData.clarityScore}
                    onChange={handleChange}
                    required
                    slotProps={{ htmlInput: { min: 1, max: 5 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PremiumSelect
                    label="O gestor deixou claro o que deveria ser feito?"
                    value={formData.clarityQuestion}
                    options={yesPartialNoOptions}
                    onChange={(value) => handleChange({ target: { name: 'clarityQuestion', value } })}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Observações"
                    name="clarityObservations"
                    value={formData.clarityObservations}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          {/* Disponibilidade e suporte */}
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Suporte
                </Typography>
                <Typography variant="h6" component="h2">
                  Disponibilidade e suporte
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Score de disponibilidade (1–5)"
                    name="availabilityScore"
                    value={formData.availabilityScore}
                    onChange={handleChange}
                    required
                    slotProps={{ htmlInput: { min: 1, max: 5 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PremiumSelect
                    label="O gestor ajudou a resolver bloqueios?"
                    value={formData.availabilityQuestion}
                    options={yesPartialNoOptions}
                    onChange={(value) => handleChange({ target: { name: 'availabilityQuestion', value } })}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Observações"
                    name="availabilityObservations"
                    value={formData.availabilityObservations}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          {/* Qualidade do feedback e desenvolvimento */}
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Evolução
                </Typography>
                <Typography variant="h6" component="h2">
                  Qualidade do feedback e desenvolvimento
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Score de qualidade do feedback (1–5)"
                    name="feedbackQualityScore"
                    value={formData.feedbackQualityScore}
                    onChange={handleChange}
                    required
                    slotProps={{ htmlInput: { min: 1, max: 5 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PremiumSelect
                    label="O feedback ajudou você a evoluir?"
                    value={formData.feedbackQualityQuestion}
                    options={yesPartialNoOptions}
                    onChange={(value) => handleChange({ target: { name: 'feedbackQualityQuestion', value } })}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Observações"
                    name="feedbackQualityObservations"
                    value={formData.feedbackQualityObservations}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          {/* Resumo final */}
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Consolidado
                </Typography>
                <Typography variant="h6" component="h2">
                  Resumo final
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="O que o gestor faz bem"
                    name="whatManagerDoesWell"
                    value={formData.whatManagerDoesWell}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="O que pode melhorar"
                    name="whatCanImprove"
                    value={formData.whatCanImprove}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Sugestões práticas"
                    name="suggestions"
                    value={formData.suggestions}
                    onChange={handleChange}
                    required
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'flex-end' }}
          >
            <Button
              type="button"
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={() => navigate('/feedbacks')}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={loading} startIcon={<SaveIcon />}>
              {loading ? 'Salvando...' : 'Salvar feedback reverso'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
    </PageContainer>
  );
};

export default CreateReverseFeedback;
